// [src/pages/FindPassword/FindPassword.jsx]

// useRef, useEffect 추가
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "config/apiConfig";

// CSS imports
import 'styles/Font.css';

// Image imports
import remoteNum from "assets/images/icon/input-delete-icon.svg";

// Component imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
// import usePreventZoom from "hooks/usePreventZoom";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import useVerificationTimer from "hooks/useVerificationTimer";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

function FindPassword() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const navigate = useNavigate();

    // 동적 높이 측정을 위한 ref 및 state 선언
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // --- State ---
    const [name, setUserName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [authNumber, setAuthNumber] = useState('');

    const [isCodeSent, setIsCodeSent] = useState(false); // 인증번호 발송 여부
    const [isVerified, setIsVerified] = useState(false); // 인증 확인 여부
    const [isLoading, setIsLoading] = useState(false);   // 로딩 상태

    // 포커스 상태 관리
    const [focusedInput, setFocusedInput] = useState(null);

    // 모달 상태
    const [modalInfo, setModalInfo] = useState(null);

    // 타이머 훅
    const { formattedTime, handleResendClick } = useVerificationTimer(isCodeSent);

    // 헤더 높이 측정 로직 추가
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 초기 로드 시 측정
        measureHeight();

        // 폰트 로딩 등으로 인한 레이아웃 시프트 대비 (0.1초 뒤 재측정)
        setTimeout(measureHeight, 100);

        // 창 크기 변경 시 측정
        window.addEventListener('resize', measureHeight);

        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // --- Handlers ---

    // 입력 핸들러
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'name') setUserName(value);
        if (name === 'phoneNumber') setPhoneNumber(value.replace(/[^0-9]/g, ''));
        if (name === 'authNumber') {
            setAuthNumber(value.replace(/[^0-9]/g, ''));
            // 인증번호 수정 시 인증 상태 초기화
            if (isVerified) setIsVerified(false);
        }
    };

    // [API 1] 인증번호 전송 (/sendsms)
    const handleRequestCode = async (isResend = false) => {
        if (phoneNumber.length < 10) {
            setModalInfo({ title: "알림", message: "올바른 휴대폰 번호를 입력해주세요.", showCancel: false });
            return;
        }
        if (isLoading) return;

        setIsLoading(true);
        // 재전송 시 인증 상태 및 입력값 초기화
        if (isResend) {
            setIsVerified(false);
            setAuthNumber('');
            setUserName('');
        }

        try {
            const response = await fetch(`${API_BASE_URL}/sendsms`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ phoneNumber: phoneNumber, pass: "Y" }),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || '인증번호 전송 실패');
            }

            // 성공 시 처리
            if (isResend) {
                handleResendClick(); // 타이머 리셋
                setModalInfo({ title: "알림", message: "인증번호가 재전송되었습니다.", showCancel: false });
            } else {
                setIsCodeSent(true); // 입력창 활성화
                setModalInfo({ title: "알림", message: "인증번호가 발송되었습니다.", showCancel: false });
            }

        } catch (error) {
            console.error("SMS Send Error:", error);
            setModalInfo({ title: "오류", message: error.message || "서버 통신 오류가 발생했습니다.", showCancel: false });
        } finally {
            setIsLoading(false);
        }
    };

    // [API 2] 인증번호 확인 (/verifysms)
    const handleVerifyCode = async () => {
        if (!authNumber || authNumber.length < 4) {
            setModalInfo({ title: "알림", message: "인증번호를 입력해주세요.", showCancel: false });
            return;
        }
        if (isLoading) return;

        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/verifysms`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    phoneNumber: phoneNumber,
                    authCode: authNumber
                })
            });

            const data = await response.json();
            const verifiData = data.data;

            // 인증 성공 확인 (code 200)
            if (response.ok && (verifiData.code === 200 || verifiData.code === '200')) {
                setIsVerified(true); // 인증 완료 상태로 변경 -> 이름 입력창 등장
                setModalInfo({
                    title: "알림",
                    message: "인증이 완료되었습니다.\n이름을 입력해주세요.",
                    showCancel: false
                });
            } else {
                setIsVerified(false);
                throw new Error(data.message || "인증번호가 일치하지 않습니다.");
            }

        } catch (error) {
            console.error("Verification Error:", error);
            setModalInfo({
                title: "오류",
                message: error.message || "인증 확인 중 오류가 발생했습니다.",
                showCancel: false
            });
        } finally {
            setIsLoading(false);
        }
    };

    // [API 3] 비밀번호 초기화 요청 (/repassword)
    const handleResetPassword = async () => {
        if (!isVerified) {
            setModalInfo({ title: "알림", message: "먼저 휴대폰 인증을 완료해주세요.", showCancel: false });
            return;
        }
        if (!name.trim()) {
            setModalInfo({ title: "알림", message: "이름을 입력해주세요.", showCancel: false });
            return;
        }
        if (isLoading) return;

        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/repassword`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded' // JSON 전송
                },
                body: new URLSearchParams({
                    phoneNumber: phoneNumber,
                    name: name
                })
            });

            const data = await response.json();
            const repwData = data.data;

            // 초기화 성공 체크
            if (response.ok && (repwData.code === 200 || repwData.code === '200' || repwData.status === 'success')) {
                setModalInfo({
                    type: 'success', // 성공 타입 지정
                    title: "비밀번호 초기화 완료",
                    message: "임시 비밀번호가 문자로 발송되었습니다.\n로그인 후 비밀번호를 변경해주세요.",
                    showCancel: false
                });
            } else {
                throw new Error(repwData.massage || repwData.message || "비밀번호 초기화에 실패했습니다.");
            }

        } catch (error) {
            console.error("Reset Password Error:", error);
            setModalInfo({
                title: "오류",
                message: error.message || "초기화 중 오류가 발생했습니다.",
                showCancel: false
            });
        } finally {
            setIsLoading(false);
        }
    };

    // 모달 닫기 / 확인 버튼 핸들러
    const handleModalClose = () => {
        // 성공 모달이었다면 로그인 페이지로 이동
        if (modalInfo?.type === 'success') {
            navigate('/');
        }
        setModalInfo(null);
    };

    // 버튼 활성화 조건
    const isRequestBtnActive = phoneNumber.length >= 10 && !isCodeSent;
    const isVerifyBtnActive = isCodeSent && authNumber.length >= 4 && !isVerified;
    const isResetBtnActive = isVerified && name.trim().length > 0;

    // 동적 스타일 객체 생성
    const dynamicMainStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        <div style={staticStyles.backgroundWrapper}>
            {/* 헤더에 ref 연결 */}
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div style={staticStyles.statusBar} />
                <TopNavigator title="비밀번호 찾기" />
            </div>

            {/* [수정 6] dynamicMainStyle 적용 */}
            <main style={dynamicMainStyle}>
                {/* 안내 문구 */}
                <div style={staticStyles.infoField}>
                    <p style={staticStyles.infoTitle}>비밀번호를 잊으셨나요?</p>
                    <p style={staticStyles.infoSubtitle}>
                        가입하신 휴대폰 번호로 인증 후<br />
                        이름을 입력하면 임시 비밀번호를 발송해 드립니다.
                    </p>
                </div>

                {/* 입력 폼 */}
                <div style={staticStyles.formContainer}>

                    {/* 휴대폰 번호 */}
                    <label style={staticStyles.inputLabel}>휴대폰번호</label>
                    <div style={staticStyles.inputWithButton}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                className="common-input"
                                style={{
                                    ...staticStyles.commonInput,
                                    ...(focusedInput === 'phoneNumber' ? staticStyles.inputFocused : {})
                                }}
                                type="tel"
                                name="phoneNumber"
                                placeholder="숫자만 입력"
                                value={phoneNumber}
                                onChange={handleChange}
                                disabled={isCodeSent} // 인증번호 발송 후 수정 불가
                                inputMode="numeric"
                                onFocus={() => setFocusedInput('phoneNumber')}
                                onBlur={() => setFocusedInput(null)}
                            />
                            {/* 지우기 버튼 */}
                            {phoneNumber.length > 0 && !isCodeSent && (
                                <img
                                    src={remoteNum}
                                    alt="지우기"
                                    style={staticStyles.clearButton}
                                    onClick={() => setPhoneNumber('')}
                                />
                            )}
                        </div>
                    </div>

                    {/* 인증번호 요청/재요청 버튼 */}
                    {!isCodeSent ? (
                        <button
                            style={{
                                ...staticStyles.textButton,
                                ...(isRequestBtnActive ? { color: '#8052FF', cursor: 'pointer' } : {})
                            }}
                            disabled={!isRequestBtnActive || isLoading}
                            onClick={() => handleRequestCode(false)}
                        >
                            {isLoading ? '전송 중...' : '인증번호 받기'}
                        </button>
                    ) : (
                        <div style={staticStyles.resendWrapper}>
                            <span style={staticStyles.resendText}>인증번호가 오지 않나요?</span>
                            <button
                                style={staticStyles.resendButton}
                                onClick={() => handleRequestCode(true)}
                                disabled={isLoading}
                            >
                                재전송
                            </button>
                        </div>
                    )}

                    {/* 인증번호 입력 + 확인 버튼 */}
                    {isCodeSent && (
                        <div style={{ marginTop: '12px' }}>
                            <div style={staticStyles.inputWithButton}>
                                <div style={{ position: 'relative', flex: 1 }}>
                                    <input
                                        className="common-input"
                                        style={{
                                            ...staticStyles.commonInput,
                                            ...(focusedInput === 'authNumber' ? staticStyles.inputFocused : {}),
                                            // 인증 완료 시 배경색 변경
                                            ...(isVerified ? {
                                                color: '#9A9A9A',
                                                background: 'linear-gradient(#1B1931, #1B1931), linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
                                            } : {})
                                        }}
                                        type="tel"
                                        name="authNumber"
                                        placeholder="인증번호 6자리"
                                        value={authNumber}
                                        onChange={handleChange}
                                        inputMode="numeric"
                                        maxLength={6}
                                        disabled={isVerified} // 인증 완료 시 수정 불가
                                        onFocus={() => setFocusedInput('authNumber')}
                                        onBlur={() => setFocusedInput(null)}
                                    />
                                    {/* 타이머 (인증 전일때만) */}
                                    {!isVerified && (
                                        <span style={staticStyles.timer}>{formattedTime}</span>
                                    )}
                                </div>

                                {/* 인증 확인 버튼 */}
                                <button
                                    style={{
                                        ...staticStyles.smallButton,
                                        ...((isVerifyBtnActive && !isLoading) ? staticStyles.smallButtonActive : {})
                                    }}
                                    disabled={!isVerifyBtnActive || isLoading || isVerified}
                                    onClick={handleVerifyCode}
                                >
                                    {isVerified ? '완료' : '확인'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 이름 입력창: 인증 완료(isVerified) 시에만 표시 */}
                    {isVerified && (
                        <div style={{ marginTop: '24px', animation: 'fadeIn 0.5s ease' }}>
                            <label style={staticStyles.inputLabel}>이름</label>
                            <input
                                className="common-input"
                                style={{
                                    ...staticStyles.commonInput,
                                    ...(focusedInput === 'name' ? staticStyles.inputFocused : {})
                                }}
                                type="text"
                                name="name"
                                placeholder="이름 입력"
                                value={name}
                                onChange={handleChange}
                                onFocus={() => setFocusedInput('name')}
                                onBlur={() => setFocusedInput(null)}
                            />
                        </div>
                    )}

                </div>
            </main>

            {/* 하단 비밀번호 초기화 버튼 */}
            <div style={staticStyles.bottomButtonWrapper}>
                <button
                    style={{
                        ...staticStyles.mainButton,
                        ...((isResetBtnActive && !isLoading) ? staticStyles.mainButtonActive : {}),
                        ...(isLoading ? { background: '#5a5a6a', cursor: 'wait' } : {})
                    }}
                    disabled={!isResetBtnActive || isLoading}
                    onClick={handleResetPassword}
                >
                    <span style={staticStyles.mainButtonText}>
                        {isLoading ? '처리 중...' : '비밀번호 초기화'}
                    </span>
                </button>
            </div>

            <ConfirmModal
                isOpen={!!modalInfo}
                onClose={handleModalClose}
                onConfirm={handleModalClose}
                title={modalInfo?.title}
                message={modalInfo?.message}
                showCancel={modalInfo?.showCancel}
            />
            {/* 페이드인 애니메이션용 스타일 태그 */}
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>
        </div>
    );
}

// Styles
const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
    },
    fixedHeaderWrapper: {
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        zIndex: 100,
        backgroundColor: '#0D042D',
    },
    statusBar: {
        flexShrink: 0,
        backgroundColor: 'transparent',
    },
    mainContent: {
        flex: 1,
        overflowY: 'auto',
        width: '100%',
        maxWidth: '430px',
        margin: '0 auto',
        // [설명] 동적 스타일에서 값을 덮어씌우므로 여기선 0 또는 기본값 유지
        paddingTop: '0',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 24px) + 94px)',
        paddingLeft: '24px',
        paddingRight: '24px',
        boxSizing: 'border-box',
    },
    // ... 나머지 스타일은 동일 ...
    infoField: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: '40px',
        marginTop: '25px',
    },
    infoTitle: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '700',
        fontSize: '24px',
        color: '#F5F8F9',
        marginBottom: '12px',
        lineHeight: '1.4',
    },
    infoSubtitle: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: '16px',
        color: '#9A9A9A',
        lineHeight: '1.5',
        margin: 0,
    },
    formContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    inputLabel: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 500,
        fontSize: '14px',
        color: '#9A9A9A',
        marginBottom: '8px',
        display: 'block',
    },
    commonInput: {
        height: '56px',
        padding: '0 16px',
        fontSize: '16px',
        borderRadius: '6px',
        border: '1px solid transparent',
        width: '100%',
        boxSizing: 'border-box',
        background: '#1B1931',
        color: '#FCFCFA',
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        outline: 'none',
        '::placeholder': { color: '#747474' },
    },
    inputFocused: {
        background: 'linear-gradient(#1B1931, #1B1931), linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
    },
    inputWithButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    clearButton: {
        position: 'absolute',
        right: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '20px',
        height: '20px',
        cursor: 'pointer',
    },
    textButton: {
        marginTop: '8px',
        background: 'transparent',
        border: 'none',
        color: '#747474',
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: '14px',
        cursor: 'default',
        textAlign: 'right',
        width: '100%',
        padding: 0,
    },
    smallButton: {
        height: '56px',
        width: '80px',
        borderRadius: '6px',
        border: 'none',
        background: '#3A394A',
        color: '#FFFFFF',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'not-allowed',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: '"Pretendard", sans-serif',
        flexShrink: 0,
    },
    smallButtonActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        cursor: 'pointer',
    },
    resendWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: '8px',
        gap: '8px',
    },
    resendText: {
        color: '#747474',
        fontSize: '13px',
    },
    resendButton: {
        background: 'transparent',
        border: 'none',
        color: '#8052FF',
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: '14px',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: 0,
    },
    timer: {
        position: 'absolute',
        right: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: '14px',
        color: '#FFFFFF',
    },
    bottomButtonWrapper: {
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        padding: '16px 24px calc(env(safe-area-inset-bottom, 24px) + 16px)',
        boxSizing: 'border-box',
        zIndex: 100,
        backgroundColor: '#0D042D',
    },
    mainButton: {
        width: '100%',
        height: '56px',
        border: 'none',
        borderRadius: '6px',
        background: '#3A394A',
        cursor: 'not-allowed',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    mainButtonActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        cursor: 'pointer',
    },
    mainButtonText: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '700',
        fontSize: '16px',
        color: '#FFFFFF',
    }
};

export default FindPassword;